import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import { AvailabilityManagerPageProps } from './AvailabilityManagerPage.types';

const AvailabilityManagerPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();

  const [RemoteAvailabilityManager, setRemoteAvailabilityManager] =
    React.useState<React.ComponentType<AvailabilityManagerPageProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadAvailabilityManager = async (): Promise<void> => {
      try {
        const { default: Router } =
          await import('schedule_mfe/AvailabilityManagerPage');
        setRemoteAvailabilityManager(() => Router);
      } catch (error) {
        console.error('Error loading AvailabilityManager: ', error);
        setLoadError(error as Error);
      }
    };

    loadAvailabilityManager();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteAvailabilityManager) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteAvailabilityManager
        msalInstance={instance}
        user={authData}
        permissions={permissions}
      />
    </React.Suspense>
  );
});

AvailabilityManagerPage.displayName = 'AvailabilityManagerPage';

export { AvailabilityManagerPage };
